/**
 * Campaign Analytics Detail Page
 * 
 * Phase 8.0: Detailed analytics for a specific campaign
 */

import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Spinner,
  List,
} from "@shopify/polaris";
import { getCampaignAnalytics, type CampaignMetrics } from "../api/analytics";

function CampaignAnalyticsPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const [metrics, setMetrics] = useState<CampaignMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!campaignId) {
      setError("Campaign ID is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getCampaignAnalytics(campaignId);
      setMetrics(data);
    } catch (err) {
      console.error("Failed to fetch campaign analytics:", err);
      setError(err instanceof Error ? err.message : "Failed to load campaign analytics.");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <Page title="Campaign Analytics">
        <Card>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Campaign Analytics">
        <Banner tone="critical">{error}</Banner>
      </Page>
    );
  }

  if (!metrics) {
    return (
      <Page title="Campaign Analytics">
        <Banner tone="info">No analytics data available for this campaign.</Banner>
      </Page>
    );
  }

  return (
    <Page
      title={`Analytics for "${metrics.campaignName}"`}
      subtitle="Detailed performance metrics for this campaign"
      backAction={{ content: "Campaigns", url: "/campaigns" }}
    >
      <BlockStack gap="400">
        <InlineStack gap="400" wrap>
          {/* Metric Cards */}
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Total Plays
              </Text>
              <Text as="p" variant="heading2xl" fontWeight="bold">
                {metrics.totalPlays.toLocaleString()}
              </Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Unique Players
              </Text>
              <Text as="p" variant="heading2xl" fontWeight="bold">
                {metrics.uniquePlayers.toLocaleString()}
              </Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Rewards Granted
              </Text>
              <Text as="p" variant="heading2xl" fontWeight="bold">
                {metrics.rewardsGranted.toLocaleString()}
              </Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Conversion Rate
              </Text>
              <Text as="p" variant="heading2xl" fontWeight="bold">
                {metrics.conversionRate.toFixed(2)}%
              </Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Avg. Session Duration
              </Text>
              <Text as="p" variant="heading2xl" fontWeight="bold">
                {metrics.avgSessionDuration.toFixed(1)}s
              </Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Rewards Rejected
              </Text>
              <Text as="p" variant="heading2xl" fontWeight="bold">
                {metrics.rewardsRejected.toLocaleString()}
              </Text>
            </BlockStack>
          </Card>
        </InlineStack>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Outcome Distribution
            </Text>
            {metrics.outcomeDistribution.length > 0 ? (
              <List type="bullet">
                {metrics.outcomeDistribution.map((outcome, index) => (
                  <List.Item key={index}>
                    <InlineStack gap="200">
                      <Text as="p" fontWeight="semibold">
                        {outcome.outcomeKey}:
                      </Text>
                      <Text as="p">
                        {outcome.count.toLocaleString()} plays ({outcome.percentage.toFixed(1)}%)
                      </Text>
                    </InlineStack>
                  </List.Item>
                ))}
              </List>
            ) : (
              <Text as="p" tone="subdued">
                No outcome data yet.
              </Text>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Recent Activity (Last 7 Days)
            </Text>
            {metrics.recentActivity.length > 0 ? (
              <List type="bullet">
                {metrics.recentActivity.map((activity, index) => (
                  <List.Item key={index}>
                    <InlineStack gap="200">
                      <Text as="p" tone="subdued">
                        {new Date(activity.timestamp).toLocaleString()}:
                      </Text>
                      <Text as="p" fontWeight="semibold">
                        {activity.eventType}
                      </Text>
                      {activity.outcomeKey && <Text as="p">({activity.outcomeKey})</Text>}
                    </InlineStack>
                  </List.Item>
                ))}
              </List>
            ) : (
              <Text as="p" tone="subdued">
                No recent activity.
              </Text>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default CampaignAnalyticsPage;
